/**
 * Component Name: OnLoadClass
 *
 * Description:
 * The OnLoadClass component adds a `loaded` class to the `<body>` element
 * shortly after the component is mounted. This is primarily used to enable
 * onLoad transitions, allowing developers to leverage CSS transitions targeting
 * the `.loaded` class instead of creating custom animations for simple property changes.
 *
 * Usage:
 * Include this component in your app layout or on specific pages where you
 * want to add the `loaded` class to the `<body>` element for transition effects.
 *
 * Example:
 * <OnLoadClass />
 *
 * Props:
 * None.
 *
 * Features:
 * - Waits for 100ms after the component is mounted before adding the `loaded` class.
 * - Ensures proper cleanup of the timer to avoid potential memory leaks.
 *
 * Notes:
 * - This is best used for initiating simple CSS property transitions like opacity, scale, or color changes.
 * - The delay can be adjusted by modifying the `setTimeout` duration (currently 100ms).
 * - This component does not render any UI elements, as it is designed to modify global styles via the `<body>` class.
 * - Ensure your CSS includes styles targeting the `.loaded` class for desired effects.
 *
 * Example CSS:
 * ```css
 * body {
 *     opacity: 0;
 *     transition: opacity 0.5s ease-in-out;
 * }
 *
 * body.loaded {
 *     opacity: 1;
 * }
 * ```
 */

import { useEffect } from 'react';

const OnLoadClass: React.FC = () => {
  useEffect(() => {
    const timer = setTimeout(() => {
      document.body.classList.add('loaded');
    }, 100); // Wait for 100ms

    return () => clearTimeout(timer); // Cleanup timer on unmount
  }, []);

  return null; // This component doesn't render anything
};

export default OnLoadClass;
